import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Label } from "@/components/ui/form/Label";
const textareaVariants = cva("border w-full rounded-xl px-5 py-4 text-base font-weight-regular transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-2 min-h-[120px]", {
  variants: {
    color: {
      purple: "border-purple bg-transparent text-black focus:outline-purple",
      yellow: "border-yellow bg-transparent text-black focus:outline-yellow",
      dark: "bg-black/40 backdrop-blur-md text-white border-white/60 placeholder:text-white/70 focus:outline-yellow"
    }
  },
  defaultVariants: {
    color: "purple"
  }
});
export interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "color">, VariantProps<typeof textareaVariants> {
  /** Error message displayed below the textarea */
  error?: string;
  /** Label for the textarea */
  label?: string;
}

/**
 * Textarea component for multi-line text input.
 * Supports color variants, labels, and error states.
 */
export const Textarea: React.FC<TextareaProps> = t0 => {
  const $ = _c(28);
  let className;
  let error;
  let id;
  let label;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      color: t1,
      error,
      label,
      className,
      id,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = error;
    $[3] = id;
    $[4] = label;
    $[5] = props;
    $[6] = t1;
  } else {
    className = $[1];
    error = $[2];
    id = $[3];
    label = $[4];
    props = $[5];
    t1 = $[6];
  }
  const color = t1 === undefined ? "purple" : t1;
  const textareaId = id || props.name;
  let t2;
  if ($[7] !== color || $[8] !== label || $[9] !== props || $[10] !== textareaId) {
    t2 = label && <Label htmlFor={textareaId} color={color || "purple"} disabled={props.disabled} className="mx-5">{label}</Label>;
    $[7] = color;
    $[8] = label;
    $[9] = props;
    $[10] = textareaId;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = `${error ? "border-red-500! focus:outline-red-500!" : ""} ${className || ""}`;
  let t4;
  if ($[12] !== color || $[13] !== t3) {
    t4 = textareaVariants({
      color,
      className: t3
    });
    $[12] = color;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = !!error;
  const t6 = error && textareaId ? `${textareaId}-error` : props["aria-describedby"];
  let t7;
  if ($[15] !== props || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== textareaId) {
    t7 = <textarea {...props} id={textareaId} rows={4} className={t4} aria-invalid={t5} aria-describedby={t6} />;
    $[15] = props;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = textareaId;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== error || $[22] !== textareaId) {
    t8 = error && <span id={textareaId ? `${textareaId}-error` : undefined} className="text-red-500 text-sm ml-4" role="alert">{error}</span>;
    $[21] = error;
    $[22] = textareaId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t2 || $[25] !== t7 || $[26] !== t8) {
    t9 = <div className="flex flex-col gap-xs w-full">{t2}{t7}{t8}</div>;
    $[24] = t2;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
};